import axios from 'axios';
import React, {useState, useEffect, useRef, Suspense, lazy} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {Box, Button, Slide, Fade, Grow, Typography} from '@material-ui/core';
import {ReactComponent as TextIcon} from '../assets/phone-icon.svg';
import ContactWidgetStyles from '../styles/contactWidgetStyles';
import Header from '../components/header';
import Home from '../components/home';
import Footer from '../components/footer';
import ContactForm from '../components/contactForm';
import CallRequestForm from '../components/callRequestForm';
import PrimaryCta from '../components/primaryCta';
import ProfileSelect from '../components/profileSelect';
import Views from '../lib/views';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Logo from '../components/logo';
import Icons from '../lib/icons';
import {Localization, __} from '../lib/localization';

const useStyles = makeStyles(ContactWidgetStyles);

export const ContactWidget = (props) => {
    const rootData = {...props.data, ...{}};

    const getDefaultView = () => {
        if (rootData.isMultiProfile) {
            return Views.profileSelect;
        } else if (rootData.hasChannels) {
            return Views.home;
        } else {
            return Views.contactForm;
        }
    }

    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    const setActiveProfile = (profile) => {
        if (profile) {
            setViewData({
                ...rootData, ...{
                    isLoading: true, activeProfile: profile, logo: null,
                }
            });
            sleep(0).then(() => { // todo -- remove this; only for testing
                axios
                    .get(props.data.apiUrl + '/widgets/' + profile.uuid)
                    .then(res => {
                        let widgetData = {
                            ...res.data, ...{
                                activeProfile: profile,
                                channels: res.data?.channels,
                                hasChannels: res.data?.channels?.length > 1 && !res.data?.text_only,
                            }
                        }
                        // if the new profile has a language set, use that. Otherwise leave it as it was.
                        if (res.data?.language) {
                            Localization.setLang(res.data?.language);
                        }
                        setViewData(widgetData);
                        setChannels(widgetData.channels);
                        props.Analytics.setProfile(res.data?.vcard?.org ?? null);
                    })
                    .catch(err => {
                        /* TODO: SEE WHAT TO DO WITH THESE POSSIBLE ERRORS */
                        console.log(err);
                    })
            });
        } else {
            setViewData(rootData);
            props.Analytics.setProfile(rootData?.vcard?.org ?? null);
        }
    }

    const getViewState = () => {
        return view;
    }

    const goBack = (viewData, currentView) => {
        let newView = Views.home;

        if (overrideBackView) {
            newView = overrideBackView;
        } else {
            newView = ((currentView === Views.contactForm || currentView === Views.callRequestForm) && viewData.hasChannels ? (Views.home) : Views.profileSelect);
        }
        setView(newView);
        setOverrideBackView(null);
    };

    const setView = (view, profile = {}) => {
        setIsShowHeaderBackArrow(false);
        setIsHeaderCondensed(false);
        setHeaderContentStyle('');

        switch (view) {
            case Views.home:
                if (viewData.activeProfile) {
                    setHeaderTitle(`<strong>${viewData.activeProfile.label}</strong>`);
                    //setHeaderContent(viewData.activeProfile.label);
                    setHeaderContent(false);
                    setIsShowHeaderBackArrow(true);
                    setHeaderContentStyle('small');
                } else {
                    setHeaderTitle(rootData.teaser ? rootData.teaser : __('leadbox_teaser'));
                    setHeaderContent(false)
                }
                break;
            case Views.contactForm:
                if (viewData.activeProfile) {
                    setHeaderTitle(`<strong>${viewData.activeProfile.label}</strong>`);
                    // setHeaderContent(viewData.activeProfile.label);
                    setHeaderContent(false);
                    setHeaderContentStyle('small');
                } else {
                    !viewData.hasChannels && rootData.teaser ? setHeaderTitle(rootData.teaser) : setHeaderTitle(__('leadbox_teaser'));

                    setHeaderContent(false);
                }
                if (viewData.activeProfile || viewData.hasChannels) {
                    setIsShowHeaderBackArrow(true);
                }
                break;
            case Views.callRequestForm:
                if (viewData.activeProfile) {
                    setHeaderTitle(`<strong>${viewData.activeProfile.label}</strong>`);
                    setHeaderContent(false);
                    setHeaderContentStyle('small');

                } else {
                    setHeaderTitle(__('call_request_teaser'));
                    setHeaderContent(false);
                }
                setIsHeaderCondensed(true);
                if (viewData.activeProfile || viewData.hasChannels) {
                    setIsShowHeaderBackArrow(true);
                }
                break;
            case Views.profileSelect:
                setActiveProfile(null);
                setHeaderTitle(rootData.headline_text ?? __('leadbox_multi_teaser'));
                setHeaderContent(false);
                setIsHeaderCondensed(rootData?.leadboxes?.length > 4);
                break;
            case Views.loading:
                if (viewData.activeProfile) {
                    setIsShowHeaderBackArrow(true);
                    setHeaderContentStyle('small');
                    setHeaderTitle(`<strong>${viewData.activeProfile.label}</strong>`);
                    // setHeaderContent(viewData.activeProfile.label);
                    setHeaderContent(false);
                }
                break;
            case Views.smsPrivacyPolicy:
                if (getViewState() !== view && getViewState() !== Views.loading) {
                    setOverrideBackView(getViewState());
                }
                setIsShowHeaderBackArrow(true);
                setHeaderContentStyle('small');
                setHeaderTitle(`<strong>${__('leadbox_sms_privacy_policy_headline')}</strong>`);
                setIsHeaderCondensed(true);
                break;
            default:
                console.error(`requested view ${view} not found`);
                view = Views.contactForm;
                break;
        }

        setViewState(view);
    }

    const [viewData, setViewData] = useState(rootData);
    const getColor = () => viewData.color;
    const accentColor = getColor() === "#FFFFFF" ? "#3C1233" : getColor();
    const rootAccentColor = rootData.color === "#FFFFFF" ? "#3C1233" : rootData.color;
    const hexToRgb = hex => hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => '#' + r + r + g + g + b + b)
        .substring(1).match(/.{2}/g)
        .map(x => parseInt(x, 16));
    const rgbOriginalColor = hexToRgb(accentColor);
    const initialColor = `rgba(${rgbOriginalColor[0]}, ${rgbOriginalColor[1]}, ${rgbOriginalColor[2]}, 0.8)`;
    const endColor = `rgba(${rgbOriginalColor[0]}, ${rgbOriginalColor[1]}, ${rgbOriginalColor[2]}, 1)`;

    const [showTooltip, setShowTooltip] = useState(false);
    const [headerTitle, setHeaderTitle] = useState('');
    const [headerContent, setHeaderContent] = useState('');
    const [isHoverClose, setIsHoverClose] = useState(false);
    const [isHoverTextWithUs, setIsHoverTextWithUs] = useState(false);
    const [showTextButton, setShowTextButton] = useState(false);
    const [showMainButton, setShowMainButton] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [isShowHeaderBackArrow, setIsShowHeaderBackArrow] = useState(false);
    const [headerContentStyle, setHeaderContentStyle] = useState();
    const [isHeaderCondensed, setIsHeaderCondensed] = useState(false);
    const [view, setViewState] = useState(getDefaultView());
    const [overrideBackView, setOverrideBackView] = useState(null);
    const showSlideRef = useRef(isOpen);
    const mainWrapperRef = useRef(null);
    const [isMobile, setIsMobile] = useState(false);
    const [isHideLogo, setIsHideLogo] = useState(false);
    const [isHideTextWithUsOnLoad, setisHideTextWithUsOnLoad] = useState(false);
    const [channels, setChannels] = useState(viewData.channels);
    const [vcardData, setVcardData] = useState({});
    const [isIconOnly, setIsIconOnly] = useState(false);
    const [isMobileHideTextButton, setIsMobileHideTextButton] = useState(false);
    const [isForceHideHighlights, setIsForceHideHighlights] = useState(false);

    showSlideRef.current = isOpen;
    /// TODO move the setIsMobile to the parent component so showHighlights can be one variable
    const styleProps = {
        isMobile: isMobile,
        isHideLogo: isHideLogo,
        isRectangleLogo: rootData.logo_style === 'rectangle',
        isHeaderCondensed: isHeaderCondensed,
        headerContentStyle: headerContentStyle,
        accentColor: accentColor,
        isLeftAligned: props.data.isLeftAligned,
        isShowHighlight: rootData.isShowHighlight && !isForceHideHighlights,
        isIconOnly: isIconOnly,
        primaryCtaWidth: Localization.getLang() === 'en' ? '192px' : '202px',
        isCallRequestView: view == Views.callRequestForm,
    };

    const classes = useStyles(styleProps);
    // TODO - this may be able to be removed in the future. The latest version of Webkit iOS introduced a bug that
    // makes the "Grow" transition flicker oddly. in the "Grow". We manually detect this webkit version to override some
    // styles.
    const isWebKit154 = () => {
        return typeof navigator !== 'undefined' && /^((?!chrome|android).)*(safari|mobile)/i.test(navigator.userAgent) && /(os |version\/)15(.|_)[4-9]/i.test(navigator.userAgent);
    }

    React.useEffect(() => {
        setView(getDefaultView());

        // This code reads parameters passed in from the loader script's URL hash
        const hash = window.location.hash;

        //This code reads the anchor link from the loader script URL
        if (window.location.hash.includes('show-sms-privacy=true')) {
            handleOpenLeadbox(Views.smsPrivacyPolicy);
        } else if (hash.includes('show-sms=true')) {
            handleOpenLeadbox(Views.contactForm);
        }

        const displayListener = window.addEventListener('message', function (e) {
            var msg = e.data ? e.data : '';

            if (msg.hasOwnProperty('open')) {
                let view;
                switch (msg.open) {
                    case 'privacy':
                        view = Views.smsPrivacyPolicy;
                        break;
                    case 'text':
                        view = Views.contactForm;
                        break;
                    case 'call-request':
                        view = Views.callRequestForm;
                        break;
                    default:
                        break;
                }
                ;handleOpenLeadbox(view);
            } else if (msg.hasOwnProperty('api')) {
                props.Analytics.trackEvent('buttons', 'custom-cta');
                switch (msg.api) {
                    case 'open-leadbox':
                        handleOpenLeadbox();
                        break;
                    case 'close-leadbox':
                        handleCloseLeadbox();
                        break;
                    case 'toggle-leadbox':
                        if (!showSlideRef.current) {
                            handleOpenLeadbox();
                        } else {
                            handleCloseLeadbox();
                        }
                        break;
                }

            }
            if (msg.hasOwnProperty('display')) {
                setIsMobile(msg.display === 'mobile');
            }
            if (msg.hasOwnProperty('isHideLogo')) {
                setIsHideLogo(msg.isHideLogo);
            }
            if (msg.hasOwnProperty('isHideTextWithUsOnLoad') && msg.isFirstLoad) {
                const isHide = msg.isHideTextWithUsOnLoad === true || msg.isHideTextWithUsOnLoad === "true";
                setisHideTextWithUsOnLoad(isHide);

                if (!isHideTextWithUsOnLoad && !isHide) {
                    setTimeout(() => {
                        showSlideRef.current === false && setShowTextButton(true);
                        (msg.display === 'mobile' && showSlideRef.current === false && !isMobileHideTextButton) && setTimeout(() => {
                            setShowTooltip(true)
                        }, 500);
                    }, 3000);
                }
            }
        }, false);

        window.parent.postMessage("initialized", '*');

        return () => {
            window.removeEventListener('resize', displayListener)
        };
    }, []);

    useEffect(() => {
        handleClose();
    }, [isHoverClose, isHoverTextWithUs])

    useEffect(() => {
        setIsIconOnly(props.data.primary_cta?.is_icon_only || (rootData.mobile_options?.primary_cta_icon_only && isMobile));
        setIsMobileHideTextButton(rootData.mobile_options?.hide_secondary_cta && isMobile);
        setIsForceHideHighlights(isMobile && rootData.mobile_options?.primary_cta_icon_only);
    }, [isMobile])

    useEffect(() => {
        if (!viewData.vcard) {
            return;
        }
        setVcardData({
            // TODO -- check at which level this info will be available; is this root-level or active location?
            org: viewData.vcard.org,
            tel: viewData.vcard.tel,
            url: viewData.vcard.url,
            adr: viewData.vcard.adr,
            message_number: viewData.vcard['tel:msg'] ?? null,
            voice_number: viewData.vcard['tel:voice'] ?? null,
            logo: viewData.logo,
        });
    }, [viewData]);

    useEffect(() => {
        handleClose();
    }, [isHoverClose, isHoverTextWithUs])

    useEffect(() => {
        if (viewData.isLoading) {
            setView(Views.loading);
        } else if (viewData.activeProfile && viewData.hasChannels) {
            setView(Views.home);
        } else if (viewData.activeProfile) {
            setView(Views.contactForm);
        }
    }, [viewData]);

    useEffect(() => {
        if (isIconOnly) {
            sendMsgToParent('isIconOnly');
        }
    }, [])

    useEffect(() => {
        sendMsgToParent({
            key: "toggleHtmlClass", data: {
                class: 'is-hide-leadbox-text-with-us', state: !showTextButton || isHideTextWithUsOnLoad
            }
        });
    }, [showTextButton, isHideTextWithUsOnLoad])

    const onTextWithUsClick = () => {
        props.Analytics.trackEvent('buttons', 'secondary-cta');
        handleOpenLeadbox();
    }

    const onPrimaryCtaClick = () => {
        props.Analytics.trackEvent('buttons', 'primary-cta');
        handleOpenLeadbox();
    }

    const onCloseClick = () => {
        props.Analytics.trackEvent('buttons', 'close-cta');
        setShowTooltip(false);
        setShowTextButton(false);
        sendMsgToParent('isHideTextWithUsOnLoad');
    }

    useEffect(() => {
        sendMsgToParent({
            key: "toggleHtmlClass", data: {
                class: 'is-hide-leadbox-text-with-us', state: !showTextButton || isHideTextWithUsOnLoad
            }
        });
    }, [showTextButton, isHideTextWithUsOnLoad])

    const handleClose = () => {
        if (!isHoverClose && !isHoverTextWithUs) {
            setShowTooltip(false)
        }
    }

    const sendMsgToParent = (msg) => {
        window.parent.postMessage(msg, '*');
    }

    const handleOpenLeadbox = async (view) => {
        await setView(view ? view : getDefaultView());
        setShowTooltip(false);
        setShowTextButton(false);
        if (!isOpen) {
            sendMsgToParent('openForm')
            setIsOpen(true);
        }
        setTimeout(() => {
            mainWrapperRef?.current?.focus(); // we focus the main content wrapper to set the tab position
            mainWrapperRef?.current?.blur(); // and then we blur so as to not show the :focus styling around the div. The tab position should still be where we want it.
            setShowMainButton(false);
        }, 125);
        props.Analytics.trackEvent('leadbox', 'impression-leadbox');
    }

    const handleCloseLeadbox = (e) => {
        if (getViewState() === Views.smsPrivacyPolicy) {
            goBack(viewData, Views.smsPrivacyPolicy);
        } else {
            props.Analytics.trackEvent('leadbox', 'close-leadbox');
            setShowTextButton(false);
            setShowTooltip(false);
            setIsOpen(false);
            setTimeout(() => {
                setShowMainButton(true);
            }, 125)
        }
    }

    const textWithUsText = rootData.secondary_cta?.text || __('leadbox_secondary_cta_text');
    const setSecondaryWidthToSameAsPrimary = !(isIconOnly) && textWithUsText.toLowerCase() === 'text with us';

    const textWithUs = <Slide
        direction={props.data?.isLeftAligned ? 'right' : 'left'}
        timeout={{enter: 500}}
        in={showTextButton}
        onEntered={() => {
            props.Analytics.trackEvent('leadbox', 'impression-secondary-cta')
        }}
    >
        <Button
            onMouseEnter={() => {
                (isMobile === false) && !isHideTextWithUsOnLoad && setShowTooltip(true);
                (isMobile === false) && setIsHoverTextWithUs(true);
            }}
            onMouseLeave={() => {
                (isMobile === false) && setTimeout(() => {
                    setIsHoverTextWithUs(false);
                }, 500)
            }}
            onClick={onTextWithUsClick}
            classes={{
                root: classes.button_small, label: classes.button_small__label, text: classes.text
            }}
            style={{
                backgroundColor: accentColor, width: (setSecondaryWidthToSameAsPrimary ? '192px' : 'auto'),
            }}
            aria-label={textWithUsText}
        >
            <span className={classes.spanText}>{textWithUsText}</span>
            <span
                className={`${classes.buttonIcon} ${(props.data.secondary_cta?.icon && Icons[props.data.secondary_cta?.icon]) ? '' : 'is-empty'}`}>
                    <Suspense fallback={<div></div>}>
                        {rootData.secondary_cta?.icon && Icons[rootData.secondary_cta?.icon] ? React.createElement(Icons[rootData.secondary_cta?.icon], {
                            style: {margin: '0 auto'}, 'aria-hidden': true, 'focusable': false
                        }) : null}
                    </Suspense>
                </span>
        </Button>
    </Slide>

    const onFormTransitionComplete = () => {
        sendMsgToParent('closeForm');
    }

    return (<div>
            <Grow
                timeout={500}
                in={isOpen}
                // See note about webkit's "Grow" glitch that the onEnter and onExit corrects
                onEnter={(node) => {
                    if (isWebKit154()) {
                        node.style.transition = 'opacity 450ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 500ms cubic-bezier(0.4, 0, 0.2, 1) 0ms';
                    }
                }}
                onExit={(node) => {
                    if (isWebKit154()) {
                        node.style.transition = 'opacity 500ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 500ms cubic-bezier(0.4, 0, 0.2, 1) 0ms';
                    }
                    ;
                }}
                onExited={onFormTransitionComplete}
                style={{transformOrigin: 'bottom center'}}
            >
                <div className={`${classes.form__container}`}>
                    <Header
                        handleCloseLeadbox={handleCloseLeadbox}
                        initialColor={initialColor}
                        endColor={endColor}
                        classes={classes}
                        logo={viewData.logo}
                        isRectangleLogo={rootData.logo_style === 'rectangle'}
                        isHeaderCondensed={isHeaderCondensed}
                        view={view}
                    >
                        <Box display="flex">
                            {isShowHeaderBackArrow &&
                                <Box alignSelf="center" className={classes.headerBackArrowBox} flexShrink={1}>
                                    <Button
                                        className={classes.headerBackArrow}
                                        onClick={() => goBack(viewData, view)}
                                        aria-label={__('leadbox_aria_back')}
                                    >
                                        <ArrowBackIcon style={{fontSize: 24}}/>
                                    </Button>
                                </Box>}
                            {(isHeaderCondensed && view !== Views.smsPrivacyPolicy) && <Box alignSelf="center">
                                <Logo
                                    src={viewData.logo}
                                    isRectangleLogo={rootData.logo_style === 'rectangle'}
                                    classes={classes}
                                />
                            </Box>}
                            <Box>
                                <Typography className={classes.headerTitle}
                                            dangerouslySetInnerHTML={{__html: headerTitle}}></Typography>
                                {headerContent && <Typography className={classes.headerContent}
                                                              dangerouslySetInnerHTML={{__html: headerContent}}></Typography>}
                            </Box>
                        </Box>
                    </Header>

                    <div ref={mainWrapperRef} tabindex="-1" className={classes.form__inner_container}>
                        {view === Views.home && <Home
                            setView={setView}
                            Analytics={props.Analytics}
                            channels={channels}
                        />}
                        {view === Views.profileSelect && <ProfileSelect
                            profiles={rootData?.leadboxes}
                            setActiveProfile={setActiveProfile}
                            buttonBackgroundColor={accentColor}
                            Analytics={props.Analytics}
                            classes={classes}
                        />}
                        {view === Views.contactForm && <ContactForm
                            accentColor={accentColor}
                            handleCloseLeadbox={handleCloseLeadbox}
                            classes={classes}
                            Analytics={props.Analytics}
                            setView={setView}
                            callFromNumber={viewData.cellphone}
                            vcardData={vcardData}
                            endpoint={`${props.data.apiUrl}/widgets/${viewData.uuid}/leads`}
                        />}
                        {view === Views.smsPrivacyPolicy && <Box
                            dangerouslySetInnerHTML={{__html: __('leadbox_sms_privacy_policy', {Business_name: vcardData.org})}}
                            className={classes.smsPrivacyPolicyContainer}>
                        </Box>}
                        {view === Views.callRequestForm && <CallRequestForm
                            accentColor={accentColor}
                            handleCloseLeadbox={handleCloseLeadbox}
                            classes={classes}
                            Analytics={props.Analytics}
                            setView={setView}
                            callFromNumber={viewData.cellphone}
                            vcardData={vcardData}
                            endpoint={`${props.data.apiUrl}/widgets/${viewData.uuid}/leads`}
                            metadata={channels.find(i => {
                                return i.type === 'call_request'
                            })?.metadata}
                            setHeaderTitle={setHeaderTitle}
                        />}
                        {view === Views.loading && <div>
                            {__('leadbox_load')}
                        </div>}
                    </div>

                    <Footer
                        classes={classes}
                        Analytics={props.Analytics}
                        setView={setView}
                    />
                </div>
            </Grow>
            {!isMobileHideTextButton && <Fade
                timeout={{appear: 1000, exit: 50}}
                in={showTooltip}
                onEntered={() => {
                    props.Analytics.trackEvent('leadbox', 'impression-close-cta')
                }}
            >
                <Button
                    onMouseEnter={() => {
                        setIsHoverClose(true);
                    }}
                    onMouseLeave={() => {
                        setIsHoverClose(false);
                    }}
                    classes={{
                        root: classes.tooltip, label: classes.tooltip__label, text: classes.text
                    }}

                    onClick={onCloseClick}
                    aria-label={__('leadbox_close')}
                >
                    {__('leadbox_close')}
                </Button>
            </Fade>}

            {(showTextButton && !isMobileHideTextButton) && textWithUs}

            <PrimaryCta
                isIconOnly={isIconOnly}
                isMobile={isMobile}
                isHideLogo={isHideLogo}
                classes={classes}
                handleCloseLeadbox={handleCloseLeadbox}
                onPrimaryCtaClick={onPrimaryCtaClick}
                showMainButton={showMainButton}
                rootAccentColor={rootAccentColor}
                primaryCta={rootData.primary_cta}
                isShowHighlight={rootData.isShowHighlight && !isForceHideHighlights}
                highlight={rootData.highlight}
            />
        </div>)
}
